import type { Key } from 'react';
import { loftColors } from '../../../antd/theme';
import {
  IntacctInvoice,
  IntacctPayment,
  IntacctPaymentAllocation,
  IntacctCreditMemo,
} from '../../../schema/generated/schema';
import { WWW_ORIGIN } from '../../../config';
import { formatCurrency, formatDateMMDDYYYY } from '../../../utils/helpers';

// ============================================================================
// Types
// ============================================================================

export type StatusFilter = 'All' | 'Paid' | 'Open' | 'Unapplied' | 'Processing';

export interface FilterState {
  searchQuery: string;
  statusFilter: StatusFilter;
  dateFrom: string | null;
  dateTo: string | null;
}

export interface HistoryPayment {
  recordNo: number;
  type: 'Payment';
  whenCreated: string;
  totalEntered: string;
  amountApplied: string;
  currency: string;
  relatedInvoiceNo: number | null;
}

export interface GroupedHistory {
  invoice: IntacctInvoice;
  appliedTransactions: HistoryPayment[];
  remainingBalance: number;
}

/** Statement tab row shape (same structure as history grouping). */
export type StatementPayment = HistoryPayment;

export interface GroupedStatement {
  invoice: IntacctInvoice;
  appliedTransactions: StatementPayment[];
  remainingBalance: number;
}

export interface DownloadableRecord {
  recordNo: number;
  docNo?: string;
  whenCreated: string;
}

/** Tree child rows for applied payments (Ant Design Table `children` on invoice rows). */
export type HistoryAppliedPaymentRow = {
  kind: 'appliedPayment';
  data: HistoryPayment;
  key: string;
};

export type HistoryTableRow =
  | {
      kind: 'invoice';
      data: GroupedHistory;
      key: string;
      /** Present when `data.appliedTransactions` is non-empty; each child is `appliedPayment`. */
      children?: HistoryTableRow[];
    }
  | { kind: 'credit'; data: HistoryPayment; key: string }
  | { kind: 'creditMemo'; data: IntacctCreditMemo; key: string }
  | HistoryAppliedPaymentRow;

// ============================================================================
// Constants
// ============================================================================

export const ROWS_PER_PAGE = 10;

export const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'All', label: 'All Statuses' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Open', label: 'Open' },
  { value: 'Unapplied', label: 'Unapplied' },
  { value: 'Processing', label: 'Processing' },
];

// ============================================================================
// Data Parsing Helpers
// ============================================================================

/**
 * Safely parse amount from API response.
 * Handles strings, numbers, null, undefined, and empty strings.
 * Prevents NaN errors when API returns unexpected formats.
 */
export const safeParseAmount = (
  value: string | number | null | undefined
): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Intacct docNo when present and non-empty; otherwise recordNo.
 * Used for analytics `order_id` (aligned with history table "Number" column).
 */
export function getInvoiceDocumentOrRecordNoForAnalytics(
  invoice: IntacctInvoice
): string {
  return invoice.docNo != null && String(invoice.docNo).trim() !== ''
    ? String(invoice.docNo).trim()
    : String(invoice.recordNo);
}

/**
 * Comma-separated display ids for selected invoice rows, in table selection order.
 */
export function getInvoiceDisplayIdsForPayNowTracking(
  tableRows: HistoryTableRow[],
  selectedKeys: readonly Key[]
): string {
  const keyToRow = new Map(tableRows.map((r) => [r.key, r]));
  const parts: string[] = [];
  for (const k of selectedKeys) {
    const row = keyToRow.get(String(k));
    if (!row || row.kind !== 'invoice') continue;
    parts.push(getInvoiceDocumentOrRecordNoForAnalytics(row.data.invoice));
  }
  return parts.join(',');
}

/**
 * Comma-separated display ids for invoice rows (e.g. Pay Now dialog line items).
 */
export function getInvoiceDisplayIdsForAnalyticsFromInvoiceRows(
  invoiceRows: HistoryTableRow[]
): string {
  const parts: string[] = [];
  for (const row of invoiceRows) {
    if (row.kind !== 'invoice') continue;
    parts.push(getInvoiceDocumentOrRecordNoForAnalytics(row.data.invoice));
  }
  return parts.join(',');
}

/**
 * Filter dates from DatePicker are stored as YYYY-MM-DD strings. Parsing with
 * `new Date('YYYY-MM-DD')` is UTC midnight and shifts the effective day in US
 * timezones. These helpers interpret the string as a local calendar day.
 */
export function localDayStartFromYyyyMmDd(yyyyMmDd: string): Date {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    const fallback = new Date(yyyyMmDd);
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export function localDayEndFromYyyyMmDd(yyyyMmDd: string): Date {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    const fallback = new Date(yyyyMmDd);
    fallback.setHours(23, 59, 59, 999);
    return fallback;
  }
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  dt.setHours(23, 59, 59, 999);
  return dt;
}

// ============================================================================
// Styling Helpers
// ============================================================================

export interface StatusBadgeStyles {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

/** Neutral pill used for all statuses except payable invoices in the history table. */
export const FINANCIALS_NEUTRAL_STATUS_BADGE: StatusBadgeStyles = {
  backgroundColor: loftColors.grays[5],
  borderColor: loftColors.grays[25],
  textColor: loftColors.grays[100],
};

/** Green highlight for open invoices that still need payment (PM emphasis). */
export const FINANCIALS_PAYABLE_STATUS_BADGE: StatusBadgeStyles = {
  backgroundColor: loftColors.greens[5],
  borderColor: loftColors.greens[25],
  textColor: loftColors.greens[100],
};

/**
 * Status-based pill styles for Statement tab and Pay Now dialog.
 * History table uses getFinancialsHistoryStatusStyles instead.
 */
export const getStatusBadgeStyles = (status: string): StatusBadgeStyles => {
  const statusLower = status.toLowerCase();

  if (statusLower === 'paid') {
    return {
      backgroundColor: loftColors.greens[5],
      borderColor: loftColors.greens[25],
      textColor: loftColors.greens[100],
    };
  }

  if (statusLower === 'open' || statusLower === 'posted') {
    return {
      backgroundColor: loftColors.blues[5],
      borderColor: loftColors.blues[25],
      textColor: loftColors.blues[100],
    };
  }

  if (statusLower === 'partial') {
    return {
      backgroundColor: '#FEF3E2',
      borderColor: '#F59E0B',
      textColor: '#D97706',
    };
  }

  if (statusLower === 'applied') {
    return {
      backgroundColor: loftColors.grays[5],
      borderColor: loftColors.grays[25],
      textColor: loftColors.grays[100],
    };
  }

  if (statusLower === 'unapplied') {
    return {
      backgroundColor: loftColors.greens[5],
      borderColor: loftColors.greens[25],
      textColor: loftColors.greens[100],
    };
  }

  if (statusLower === 'processing') {
    return {
      backgroundColor: '#F0E6FF',
      borderColor: '#9B59B6',
      textColor: '#7D3C98',
    };
  }

  return {
    backgroundColor: loftColors.grays[5],
    borderColor: loftColors.grays[25],
    textColor: loftColors.darkBlues[90],
  };
};

/** True when the row is an invoice with a balance due and eligible for Pay Now. */
export const isPayableInvoiceRow = (row: HistoryTableRow): boolean => {
  if (row.kind !== 'invoice') return false;
  const g = row.data;
  return (
    g.invoice.state.toLowerCase() !== 'paid' &&
    !g.invoice.paymentProcessingStatus &&
    g.remainingBalance > 0
  );
};

export const getFinancialsHistoryStatusStyles = (
  row: HistoryTableRow
): StatusBadgeStyles =>
  isPayableInvoiceRow(row)
    ? FINANCIALS_PAYABLE_STATUS_BADGE
    : FINANCIALS_NEUTRAL_STATUS_BADGE;

export const getFinancialsEmptyMessage = (
  filters: FilterState,
  hasAnyBillingData: boolean
): string => {
  const { statusFilter, searchQuery, dateFrom, dateTo } = filters;
  const trimmed = searchQuery.trim();
  const hasDateFilter = Boolean(dateFrom || dateTo);
  const hasExtraFilters =
    trimmed.length > 0 || hasDateFilter || statusFilter !== 'All';

  if (!hasAnyBillingData && !hasExtraFilters) {
    return "You don't have any billing activity in this account yet.";
  }

  const byStatus: Record<StatusFilter, string> = {
    All: 'We found no records matching your filters.',
    Paid: 'We found no paid invoices matching your filters.',
    Open: 'We found no open invoices matching your filters.',
    Unapplied: 'We found no unapplied credits matching your filters.',
    Processing:
      'We found no invoices with a payment in progress matching your filters.',
  };

  let msg = byStatus[statusFilter];
  if (trimmed || hasDateFilter) {
    msg += ' Try adjusting your invoice number search or date range.';
  }
  return msg;
};

// ============================================================================
// Effective Status (Intacct + Snowflake merge)
// ============================================================================

export const getEffectiveStatus = (invoice: IntacctInvoice): string => {
  if (invoice.paymentProcessingStatus === 'PROCESSING') return 'Processing';
  const s = invoice.state;
  if (s.toLowerCase() === 'posted') return 'Open';
  return s;
};

// ============================================================================
// History View Helpers
// ============================================================================

/**
 * Creates a HistoryPayment fragment from an IntacctPayment and allocation.
 */
const createHistoryPayment = (
  payment: IntacctPayment,
  invoiceNo: number | null,
  amountApplied: string
): HistoryPayment => ({
  recordNo: payment.recordNo,
  type: 'Payment',
  whenCreated: payment.whenCreated,
  totalEntered: payment.totalEntered,
  amountApplied,
  currency: payment.currency,
  relatedInvoiceNo: invoiceNo,
});

/** GraphQL may return invoice record numbers as string or number; Map keys must be consistent. */
const toInvoiceRecordNoKey = (recordNo: string | number): number => {
  const n =
    typeof recordNo === 'number' ? recordNo : parseInt(String(recordNo), 10);
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Groups invoices with their allocated payment fragments for History View.
 *
 * For each payment, iterates its allocations array and creates a "fragment"
 * per allocation, nesting it under the matching invoice. Payments with empty
 * allocations (or whose allocations reference invoices not in the current list)
 * are placed in the unapplied credits bucket.
 */
export const groupTransactionsForHistory = (
  invoices: IntacctInvoice[],
  payments: IntacctPayment[],
  filters: FilterState,
  creditMemos: IntacctCreditMemo[] = []
): {
  history: GroupedHistory[];
  unappliedCredits: HistoryPayment[];
  filteredCreditMemos: IntacctCreditMemo[];
} => {
  const invoiceMap = new Map<number, IntacctInvoice>();
  invoices.forEach((inv) => {
    const key = toInvoiceRecordNoKey(inv.recordNo);
    if (Number.isFinite(key)) {
      invoiceMap.set(key, inv);
    }
  });

  const appliedByInvoice = new Map<number, HistoryPayment[]>();
  const unappliedCredits: HistoryPayment[] = [];

  for (const payment of payments) {
    const allocations: IntacctPaymentAllocation[] = payment.allocations ?? [];

    if (allocations.length === 0) {
      unappliedCredits.push(
        createHistoryPayment(payment, null, payment.totalEntered)
      );
    } else {
      for (const alloc of allocations) {
        const invoiceNo = toInvoiceRecordNoKey(alloc.invoiceRecordNo);
        const fragment = createHistoryPayment(
          payment,
          Number.isFinite(invoiceNo) ? invoiceNo : null,
          alloc.amountApplied
        );

        if (Number.isFinite(invoiceNo) && invoiceMap.has(invoiceNo)) {
          if (!appliedByInvoice.has(invoiceNo)) {
            appliedByInvoice.set(invoiceNo, []);
          }
          const existing = appliedByInvoice.get(invoiceNo);
          if (existing) {
            existing.push(fragment);
          }
        } else {
          unappliedCredits.push(fragment);
        }
      }
    }
  }

  let filteredInvoices = invoices;

  if (filters.searchQuery) {
    const searchLower = filters.searchQuery.toLowerCase();
    filteredInvoices = filteredInvoices.filter(
      (inv) =>
        inv.recordNo.toString().includes(searchLower) ||
        inv.docNo?.toLowerCase().includes(searchLower)
    );
  }

  if (filters.statusFilter !== 'All') {
    if (filters.statusFilter === 'Processing') {
      filteredInvoices = filteredInvoices.filter(
        (inv) => inv.paymentProcessingStatus === 'PROCESSING'
      );
    } else if (filters.statusFilter === 'Open') {
      filteredInvoices = filteredInvoices.filter(
        (inv) =>
          !inv.paymentProcessingStatus &&
          (inv.state.toLowerCase() === 'open' ||
            inv.state.toLowerCase() === 'posted')
      );
    } else {
      filteredInvoices = filteredInvoices.filter(
        (inv) =>
          !inv.paymentProcessingStatus &&
          inv.state.toLowerCase() === filters.statusFilter.toLowerCase()
      );
    }
  }

  if (filters.dateFrom) {
    const from = localDayStartFromYyyyMmDd(filters.dateFrom);
    filteredInvoices = filteredInvoices.filter(
      (inv) => new Date(inv.whenCreated) >= from
    );
  }

  if (filters.dateTo) {
    const to = localDayEndFromYyyyMmDd(filters.dateTo);
    filteredInvoices = filteredInvoices.filter(
      (inv) => new Date(inv.whenCreated) <= to
    );
  }

  const history: GroupedHistory[] = filteredInvoices.map((invoice) => {
    const invoiceKey = toInvoiceRecordNoKey(invoice.recordNo);
    const appliedTransactions = Number.isFinite(invoiceKey)
      ? appliedByInvoice.get(invoiceKey) ?? []
      : [];

    // Use Intacct's authoritative outstanding balance (TOTALDUE) rather than
    // recomputing totalEntered minus payment allocations. TOTALDUE already
    // nets out payments and any applied credit memos, so it matches Intacct
    // exactly and avoids over/under-stating balances when credits are applied.
    const remainingBalance = safeParseAmount(invoice.totalDue);

    return {
      invoice,
      appliedTransactions,
      remainingBalance,
    };
  });

  history.sort(
    (a, b) =>
      new Date(b.invoice.whenCreated).getTime() -
      new Date(a.invoice.whenCreated).getTime()
  );

  let filteredUnapplied = unappliedCredits;
  if (filters.searchQuery) {
    const searchLower = filters.searchQuery.toLowerCase();
    filteredUnapplied = filteredUnapplied.filter((credit) =>
      credit.recordNo.toString().includes(searchLower)
    );
  }

  if (filters.dateFrom) {
    const from = localDayStartFromYyyyMmDd(filters.dateFrom);
    filteredUnapplied = filteredUnapplied.filter(
      (credit) => new Date(credit.whenCreated) >= from
    );
  }

  if (filters.dateTo) {
    const to = localDayEndFromYyyyMmDd(filters.dateTo);
    filteredUnapplied = filteredUnapplied.filter(
      (credit) => new Date(credit.whenCreated) <= to
    );
  }

  let filteredCreditMemos = [...creditMemos];

  if (filters.searchQuery) {
    const searchLower = filters.searchQuery.toLowerCase();
    filteredCreditMemos = filteredCreditMemos.filter(
      (cm) =>
        cm.recordNo.toString().includes(searchLower) ||
        cm.docNo?.toLowerCase().includes(searchLower)
    );
  }

  if (filters.dateFrom) {
    const from = localDayStartFromYyyyMmDd(filters.dateFrom);
    filteredCreditMemos = filteredCreditMemos.filter(
      (cm) => new Date(cm.whenCreated) >= from
    );
  }

  if (filters.dateTo) {
    const to = localDayEndFromYyyyMmDd(filters.dateTo);
    filteredCreditMemos = filteredCreditMemos.filter(
      (cm) => new Date(cm.whenCreated) <= to
    );
  }

  return { history, unappliedCredits: filteredUnapplied, filteredCreditMemos };
};

/**
 * Calculate Total Amount Due for History View.
 * Sums the remaining balance for invoices that are not fully paid.
 */
export const calculateHistoryAmountDue = (
  history: GroupedHistory[],
  creditMemos: IntacctCreditMemo[] = []
): number => {
  const invoiceBalance = history
    .filter((s) => s.invoice.state.toLowerCase() !== 'paid')
    .reduce((sum, s) => sum + s.remainingBalance, 0);

  // Subtract only the unapplied portion of each credit memo (TOTALDUE). Credits
  // already applied to invoices are reflected in each invoice's TOTALDUE, so
  // using the original totalEntered here would double-count them.
  const creditTotal = creditMemos.reduce(
    (sum, cm) => sum + Math.abs(safeParseAmount(cm.totalDue)),
    0
  );

  return invoiceBalance - creditTotal;
};

// ============================================================================
// Statement CSV export (matches History table columns)
// ============================================================================

/** Flatten parent rows in display order; applied-payment rows follow their invoice. */
export const flattenHistoryRowsForExport = (
  rows: HistoryTableRow[]
): HistoryTableRow[] => {
  const out: HistoryTableRow[] = [];
  for (const row of rows) {
    out.push(row);
    if (row.kind === 'invoice' && row.children?.length) {
      for (const child of row.children) {
        out.push(child);
      }
    }
  }
  return out;
};

const escapeCsvField = (value: string): string => {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const getHistoryRowCsvValues = (row: HistoryTableRow): string[] => {
  if (row.kind === 'appliedPayment') {
    const p = row.data;
    return [
      String(p.recordNo),
      formatDateMMDDYYYY(p.whenCreated),
      '—',
      'Applied',
      `-${formatCurrency(p.amountApplied, p.currency)}`,
      '—',
    ];
  }

  if (row.kind === 'credit') {
    const p = row.data;
    const amt = `-${formatCurrency(p.amountApplied, p.currency)}`;
    return [
      String(p.recordNo),
      formatDateMMDDYYYY(p.whenCreated),
      '—',
      'Unapplied',
      amt,
      amt,
    ];
  }

  if (row.kind === 'creditMemo') {
    const cm = row.data;
    const amount = safeParseAmount(cm.totalEntered);
    const absAmount = Math.abs(amount).toFixed(2);
    const amtStr = `-${formatCurrency(absAmount, cm.currency || 'USD')}`;
    const totalDue = safeParseAmount(cm.totalDue);
    const absValue = Math.abs(totalDue).toFixed(2);
    const prefix = totalDue < 0 ? '-' : '';
    const balStr = `${prefix}${formatCurrency(absValue, cm.currency || 'USD')}`;
    return [
      cm.docNo || String(cm.recordNo),
      formatDateMMDDYYYY(cm.whenCreated),
      '—',
      cm.state,
      amtStr,
      balStr,
    ];
  }

  if (row.kind === 'invoice') {
    const inv = row.data.invoice;
    const docNo =
      inv.docNo != null && String(inv.docNo).trim() !== ''
        ? String(inv.docNo)
        : '—';
    const balance = row.data.remainingBalance;
    const balPrefix = balance < 0 ? '-' : '';
    const balStr = `${balPrefix}${formatCurrency(
      Math.abs(balance).toFixed(2),
      inv.currency
    )}`;
    return [
      docNo,
      formatDateMMDDYYYY(inv.whenCreated),
      formatDateMMDDYYYY(inv.whenDue),
      getEffectiveStatus(inv),
      formatCurrency(inv.totalEntered, inv.currency),
      balStr,
    ];
  }

  return [];
};

export const buildHistoryStatementCsv = (rows: HistoryTableRow[]): string => {
  const flat = flattenHistoryRowsForExport(rows);
  const headers = ['Number', 'Date', 'Due Date', 'Status', 'Amount', 'Balance'];
  const lines = [headers.map(escapeCsvField).join(',')];
  for (const row of flat) {
    lines.push(getHistoryRowCsvValues(row).map(escapeCsvField).join(','));
  }
  return lines.join('\r\n');
};

export const triggerCsvDownload = (
  filename: string,
  csvContent: string
): void => {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

// ============================================================================
// Invoice PDF Download
// ============================================================================

/**
 * Downloads an invoice PDF from BAS via authenticated fetch, constructs a
 * filename in the pattern Invoice_<InvoiceNumber>_<YYYY-MM-DD>.pdf, and
 * triggers a browser download.
 */
export const handleDownloadInvoice = async (
  record: DownloadableRecord,
  billingAccountId: string
): Promise<void> => {
  const url = `${WWW_ORIGIN}/billing-account-service/getInvoicePDF/${billingAccountId}/${record.recordNo}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch invoice PDF: ${response.status} ${response.statusText}`
    );
  }

  const blob = await response.blob();
  const pdfBlob = new Blob([blob], { type: 'application/pdf' });

  const invoiceNumber = record.docNo || String(record.recordNo);
  const created = new Date(record.whenCreated);
  const formattedDate = `${created.getFullYear()}-${String(
    created.getMonth() + 1
  ).padStart(2, '0')}-${String(created.getDate()).padStart(2, '0')}`;
  const fileName = `Invoice_${invoiceNumber}_${formattedDate}.pdf`;

  const objectUrl = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};
